import React, { useState, useMemo, useEffect } from "react";
import Text from "components/common/Text";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import ButtonBase from "components/common/ButtonBase";
import JarvisButton from "components/JarvisButton";
import TextField from "components/common/TextField";
import { formatHelper } from "helper/format";
import { Chip, IconButton, CircularProgress, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferIcon from "@mui/icons-material/LocalOfferOutlined";
import { masterApis } from "stores/master/masterApis";
import CountdownTimer from "components/common/CountdownTimer";

const CouponCodeRow = (props) => {
  const {
    onActionTrigger,
    viewTypes,
    activeViewType = viewTypes.DEFAULT,
    formData,
    formError,
    discountValue,
    percentageOff,
    disableRemoveCoupon = false,
    pricingValue,
    selectedPlanType,
    propsValue,
  } = props;

  const [showCouponsModal, setShowCouponsModal] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);
  const [activeProductCode] = useState(
    localStorage.getItem("activeProduct") ?? "",
  );
  const [appliedCouponValidTime, setAppliedCouponValidTime] = useState(null);
  const [appliedCouponCode, setAppliedCouponCode] = useState("");
  const [isTimerExpired, setIsTimerExpired] = useState(false);

  // Check if payment type is UPGRADE
  const isUpgrade =
    propsValue?.paymentType === "UPGRADE" ||
    propsValue?.paymentType === "RENEWAL";

  const fetchCouponsList = async () => {
    if (!activeProductCode) return;

    setIsLoadingCoupons(true);
    try {
      const tempActiveProduct = activeProductCode
        ?.replace(/^"|"$/g, "")
        ?.trim()
        ?.toUpperCase();

      const res = await masterApis.getCouponList(
        tempActiveProduct,
        selectedPlanType?.planId,
      );

      const dynamicKey = Object.keys(res).find(
        (key) => Array.isArray(res[key]) && res[key].length > 0,
      );

      if (dynamicKey && Array.isArray(res[dynamicKey])) {
        const transformedCoupons = res[dynamicKey].map((coupon) => ({
          offer_name: coupon.offerName,
          discount_type: coupon.discountType,
          is_applicaeble: coupon.isDisplayCoupon?.data?.[0] || 0,
          offer_type: coupon.offerType,
          plan_type: coupon.planType,
          discount_value: coupon.discountValue,
          offer_code: coupon.offerCode,
          validTime: coupon.validTime,
          validOffer: coupon.validOffer,
        }));

        setAvailableCoupons(transformedCoupons);
      } else {
        setAvailableCoupons([]);
      }
    } catch (error) {
      setAvailableCoupons([]);
    } finally {
      setIsLoadingCoupons(false);
    }
  };

  useEffect(() => {
    fetchCouponsList();
  }, []);

  // Show ALL applicable coupons
  const filteredCoupons = useMemo(() => {
    const filtered = availableCoupons.filter(
      (coupon) => coupon.is_applicaeble === 1,
    );
    return filtered;
  }, [availableCoupons]);

  // Check if any coupon in filtered list has validOffer true AND validTime is in the future AND offerType is "INITIAL"
  const hasAnyValidOffer = useMemo(() => {
    // Don't show any offers if it's UPGRADE
    if (isUpgrade) return false;

    const now = new Date();
    return filteredCoupons.some((coupon) => {
      if (coupon.validOffer !== true) return false;
      if (coupon.offer_type !== "INITIAL") return false;

      // Check if validTime is in the future
      const validDate = new Date(coupon.validTime);
      return validDate > now;
    });
  }, [filteredCoupons, isUpgrade]);

  // Get the earliest valid time from coupons with validOffer true AND future dates AND offerType is "INITIAL"
  const earliestValidTime = useMemo(() => {
    // Don't show any offers if it's UPGRADE
    if (isUpgrade) return null;

    const now = new Date();
    const validCoupons = filteredCoupons.filter((coupon) => {
      if (coupon.validOffer !== true) return false;
      if (coupon.offer_type !== "INITIAL") return false;
      const validDate = new Date(coupon.validTime);
      return validDate > now;
    });

    if (validCoupons.length === 0) return null;

    // Sort by validTime and get the earliest
    const sorted = validCoupons.sort(
      (a, b) => new Date(a.validTime) - new Date(b.validTime),
    );

    return sorted[0].validTime;
  }, [filteredCoupons, isUpgrade]);

  // Check if any applied coupon has validOffer true AND validTime in future AND offerType is "INITIAL"
  const hasValidOffer = useMemo(() => {
    // Don't show any offers if it's UPGRADE
    if (isUpgrade) return false;

    if (!appliedCouponCode) return false;
    const appliedCoupon = availableCoupons.find(
      (coupon) => coupon.offer_name === appliedCouponCode,
    );

    if (!appliedCoupon || appliedCoupon.validOffer !== true) return false;
    if (appliedCoupon.offer_type !== "INITIAL") return false;

    const now = new Date();
    const validDate = new Date(appliedCoupon.validTime);
    return validDate > now;
  }, [availableCoupons, appliedCouponCode, isUpgrade]);

  // Get valid time for applied coupon
  const getAppliedCouponValidTime = useMemo(() => {
    if (!appliedCouponCode) return null;
    const appliedCoupon = availableCoupons.find(
      (coupon) => coupon.offer_name === appliedCouponCode,
    );

    return appliedCoupon?.validTime || null;
  }, [availableCoupons, appliedCouponCode]);

  const getDiscountDisplay = (offer) => {
    return offer.discount_type === "FLAT"
      ? `₹${offer.discount_value} OFF`
      : `${offer.discount_value}% OFF`;
  };

  const getPlanTypeDisplay = (planType) => {
    switch (planType) {
      case "YEARLY":
        return "Yearly Plan";
      case "HALF_YEARLY":
        return "Half Yearly Plan";
      default:
        return planType;
    }
  };

  const handleSelectCoupon = (coupon) => {
    setAppliedCouponCode(coupon.offer_code);
    setAppliedCouponValidTime(coupon.validTime);
    setIsTimerExpired(false);
    onActionTrigger("COUPON_CHANGE", {
      name: "coupon",
      value: coupon.offer_code,
    });
    setShowCouponsModal(false);
    onActionTrigger("COUPON_SUBMIT");
  };

  const handleCancel = () => {
    setAppliedCouponCode("");
    setAppliedCouponValidTime(null);
    setIsTimerExpired(false);
    onActionTrigger("COUPON_CLEAR");
    setShowCouponsModal(false);
  };

  const handleTimerExpire = () => {
    setIsTimerExpired(true);
  };

  const CouponsModal = () => {
    const now = new Date();

    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
        onClick={() => setShowCouponsModal(false)}
      >
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            maxWidth: 500,
            width: "100%",
            maxHeight: "80vh",
            overflow: "auto",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 3, borderBottom: "1px solid #E0E0E0" }}
          >
            <Text sx={{ fontSize: 20, fontWeight: 700 }}>
              Available Coupons
            </Text>
            <IconButton onClick={() => setShowCouponsModal(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Stack spacing={2} sx={{ p: 3 }}>
            {isLoadingCoupons ? (
              <Stack alignItems="center" spacing={2}>
                <CircularProgress size={40} />
                <Text>Loading coupons...</Text>
              </Stack>
            ) : filteredCoupons.length > 0 ? (
              filteredCoupons.map((offer, index) => {
                const isValid =
                  !isUpgrade &&
                  offer.validOffer === true &&
                  offer.offer_type === "INITIAL" &&
                  new Date(offer.validTime) > now;

                return (
                  <Box
                    key={index}
                    sx={{
                      border: "1px solid #E0E7FF",
                      borderRadius: 2,
                      p: 2,
                      cursor: isValid ? "pointer" : "not-allowed",
                      opacity: isValid ? 1 : 0.6,
                      "&:hover": isValid
                        ? {
                          borderColor: "#3B82F6",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }
                        : {},
                    }}
                    onClick={() => {
                      if (isValid) {
                        handleSelectCoupon(offer);
                      }
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between">
                        <Text sx={{ fontWeight: 700 }}>{offer.offer_code}</Text>
                        <Chip
                          label={getDiscountDisplay(offer)}
                          sx={{
                            backgroundColor: "#45AC70",
                            color: "white",
                            fontWeight: 600,
                          }}
                        />
                      </Stack>

                      <Text variant="small" sx={{ color: "#666" }}>
                        {getPlanTypeDisplay(offer.plan_type)}
                      </Text>

                      {offer.offer_type === "INITIAL" && (
                        <Text
                          variant="small"
                          sx={{ color: "#3B82F6", fontWeight: 500 }}
                        >
                          Initial Offer
                        </Text>
                      )}

                      {isUpgrade && (
                        <Text variant="small" sx={{ color: "#EF4444" }}>
                          Not available for upgrade
                        </Text>
                      )}

                      {!isUpgrade &&
                        !isValid &&
                        offer.validOffer === true &&
                        offer.offer_type === "INITIAL" && (
                          <Text variant="small" sx={{ color: "#EF4444" }}>
                            Offer expired
                          </Text>
                        )}

                      {!isUpgrade &&
                        !isValid &&
                        offer.offer_type !== "INITIAL" && (
                          <Text variant="small" sx={{ color: "#EF4444" }}>
                            Not eligible
                          </Text>
                        )}

                      {!isUpgrade && isValid && offer.validTime && (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Text variant="small" sx={{ color: "#D97706" }}>
                            Valid until:
                          </Text>
                          <CountdownTimer
                            targetTime={offer.validTime}
                            size="small"
                          />
                        </Stack>
                      )}

                      <ButtonBase
                        variant="outlined"
                        disabled={!isValid}
                        sx={{
                          mt: 1,
                          opacity: !isValid ? 0.5 : 1,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isValid) {
                            handleSelectCoupon(offer);
                          }
                        }}
                      >
                        {isValid
                          ? "Apply Coupon"
                          : isUpgrade
                            ? "Upgrade not eligible"
                            : offer.offer_type !== "INITIAL"
                              ? "Not Eligible"
                              : "Expired"}
                      </ButtonBase>
                    </Stack>
                  </Box>
                );
              })
            ) : (
              <Text>No coupons available</Text>
            )}
          </Stack>
        </Box>
      </Box>
    );
  };

  const CouponsHeader = () => {
    // Don't show header if it's UPGRADE or timer is expired
    if (isUpgrade || isTimerExpired) return null;

    // Define shouldShowCouponsLink here
    const shouldShowCouponsLink =
      filteredCoupons.length >= 1 && hasAnyValidOffer;

    return shouldShowCouponsLink ? (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Text variant="small" sx={{ color: "#666" }}>
          Available Initial Offers
        </Text>
        <Text
          variant="small"
          sx={{
            color: "#D97706",
            fontWeight: 500,
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={() => setShowCouponsModal(true)}
        >
          View Offers (
          {filteredCoupons.filter((c) => c.offer_type === "INITIAL").length})
        </Text>
      </Stack>
    ) : null;
  };

  // DEFAULT
  if (activeViewType === viewTypes.DEFAULT) {
    return (
      <>
        <Text sx={{ fontWeight: 700, fontSize: 18, mb: 2 }}>Summary</Text>

        <CouponsHeader />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocalOfferIcon sx={{ color: "#212225" }} />
            <Text sx={{ fontWeight: 500 }}>Coupon Discount</Text>
          </Stack>
          <ButtonBase
            variant="outlined"
            onClick={() => onActionTrigger("COUPON_ADD")}
          >
            Apply
          </ButtonBase>
        </Stack>

        {!isUpgrade &&
          !appliedCouponCode &&
          hasAnyValidOffer &&
          earliestValidTime &&
          !isTimerExpired && (
            <Stack direction="row" alignItems="center" mt={2}>
              <Text sx={{ color: "#212225", fontWeight: 500 }}>
                Coupon Valid Until:
              </Text>
              <CountdownTimer
                targetTime={earliestValidTime}
                onExpire={handleTimerExpire}
              />
            </Stack>
          )}

        {!isUpgrade &&
          appliedCouponCode &&
          hasValidOffer &&
          getAppliedCouponValidTime &&
          !isTimerExpired && (
            <Stack direction="row" alignItems="center" mt={2}>
              <Text sx={{ color: "#212225", fontWeight: 500 }}>
                Coupon Valid Until:
              </Text>
              <CountdownTimer
                targetTime={getAppliedCouponValidTime}
                onExpire={handleTimerExpire}
              />
            </Stack>
          )}

        {showCouponsModal && <CouponsModal />}
      </>
    );
  }

  // EDIT
  if (activeViewType === viewTypes.EDIT) {
    return (
      <>
        <CouponsHeader />

        <Stack direction="row" spacing={0}>
          <TextField
            placeholder="Enter Coupon Code"
            value={formData.coupon}
            onChange={(e) =>
              onActionTrigger("COUPON_CHANGE", {
                name: "coupon",
                value: e.target.value.toUpperCase(),
              })
            }
            error={formError.coupon}
            helperText={formError.coupon}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "4px 0 0 4px",
              },
            }}
          />
          <JarvisButton
            onClick={() => onActionTrigger("COUPON_SUBMIT")}
            sx={{
              borderRadius: "0 4px 4px 0",
              height: "56px",
              minWidth: "100px",
            }}
          >
            Apply
          </JarvisButton>
        </Stack>

        {!isUpgrade &&
          !appliedCouponCode &&
          hasAnyValidOffer &&
          earliestValidTime &&
          !isTimerExpired && (
            <Stack direction="row" alignItems="center" mt={2}>
              <Text sx={{ color: "#212225", fontWeight: 500 }}>
                Coupon Valid Until:
              </Text>
              <CountdownTimer
                targetTime={earliestValidTime}
                onExpire={handleTimerExpire}
              />
            </Stack>
          )}

        {/* Show timer AFTER applying coupon */}
        {!isUpgrade &&
          appliedCouponCode &&
          hasValidOffer &&
          getAppliedCouponValidTime &&
          !isTimerExpired && (
            <Stack direction="row" alignItems="center" mt={2}>
              <Text sx={{ color: "#212225", fontWeight: 500 }}>
                Coupon Valid Until:
              </Text>
              <CountdownTimer
                targetTime={getAppliedCouponValidTime}
                onExpire={handleTimerExpire}
              />
            </Stack>
          )}

        {showCouponsModal && <CouponsModal />}
      </>
    );
  }

  // APPLIED
  if (activeViewType === viewTypes.APPLIED) {
    return (
      <>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text sx={{ fontWeight: "medium" }}>{props?.formData?.coupon === "ALL_IN_ONE" ? "1 Coupon Auto-Applied" : "Initial Offer Applied"}</Text>
          <ButtonBase
            variant="text"
            onClick={handleCancel}
            disabled={disableRemoveCoupon}
          >
            Remove
          </ButtonBase>
        </Stack>

        <CouponsHeader />

        <Text variant="small" sx={{ color: "#45AC70" }}>
          {/* {props?.formData?.coupon === "ALL_IN_ONE" ? "1 Coupon Auto-Applied" : "Initial Offer Applied"} */}

          {props?.formData?.coupon === "ALL_IN_ONE" ? percentageOff : `You saved ${formatHelper.currency(discountValue)}`}


        </Text>

        {/* Show timer for applied coupon */}
        {!isUpgrade &&
          appliedCouponCode &&
          hasValidOffer &&
          getAppliedCouponValidTime &&
          !isTimerExpired && (
            <Stack direction="row" alignItems="center" mt={1}>
              <Text
                sx={{ color: "#212225", fontWeight: 500, fontSize: "14px" }}
              >
                Valid Until:
              </Text>
              <CountdownTimer
                targetTime={getAppliedCouponValidTime}
                onExpire={handleTimerExpire}
              />
            </Stack>
          )}

        {showCouponsModal && <CouponsModal />}
      </>
    );
  }

  return null;
};

export default CouponCodeRow;
